import React, { useState } from "react";
import { PrimeIcons } from "primereact/api";
import { useHistory } from "react-router-dom";

import { TawreedAction, TawreedActionName } from "../../../../../common/actions";
import { TawreedTable, TawreedTableProps } from "../../../../../common/components/table";
import { CssUtils } from "../../../../../helpers/css";
import { Routes } from "../../../../../router";
import { DeliveryPartner, DeliveryPartnerService } from "../../../domain";
import { TawreedTableFilters } from "../../../../../common/components/table/filters";
import { GlobalContext } from "../../../../../context";
import { TawreedTableFormatters } from "../../../../../common/components/table/formatters";
import { tawreedTranslationService } from "../../../../../common/translations";
import { PartnerDto } from "../../../../partners/domain";


export const DeliveryPartnerTable: React.FC = () => {
    const history = useHistory();
    const service = React.useMemo(() => new DeliveryPartnerService(), []);
    const { auth:{user}, constants: { constants } } = React.useContext(GlobalContext);
    const [reload, setReload] = useState(false);

    const onNew = (): Promise<void> => {
        return new Promise((resolve) => {
            history.push(Routes.DeliveryPartnersNew);
            resolve();
        });
    }

    const onDetails = (sender?: DeliveryPartner): Promise<void> => {
        return new Promise((resolve, reject) => {
            if (sender && sender.deliveryPartnerId) {
                history.push(Routes.DeliveryPartnersDetails.replace(':id', sender.deliveryPartnerId.toString()));
                resolve();
            } else {
                reject();
            }
        });
    }
    const onActivate = (sender?: DeliveryPartner | Array<DeliveryPartner>): Promise<void> => {
        return new Promise((resolve, reject) => {
            let partners: DeliveryPartner[] | undefined = undefined;
            if (Array.isArray(sender)) {
                partners = sender;
            } else if (sender) {
                partners = [sender];
            }
            if (!partners || !partners.length) {
                reject('No item selected');
            } else {
                setReload(false);
                service.activateAll(partners.map(e=>e.partnerId!))
                    .then((result:boolean) => {
                        if(result){
                            setReload(true);
                            resolve();
                        }
                    })
                    .catch(err => {
                        reject(err);
                    });
            }
        });
    };

    const onDeactivate = (sender?: DeliveryPartner | Array<DeliveryPartner>): Promise<void> => {
        return new Promise((resolve, reject) => {
            let partners: DeliveryPartner[] | undefined = undefined;
            if (Array.isArray(sender)) {
                partners = sender;
            } else if (sender) {
                partners = [sender];
            }
            if (!partners || !partners.length) {
                reject('No item selected');
            } else {
                setReload(false);
                service.deactivateAll(partners.map(e=>e.partnerId!))
                    .then(() => {
                        setReload(true);
                        resolve();
                    })
                    .catch(err => {
                        reject(err);
                    });
            }
        });
    };
    const onDelete = (sender?: DeliveryPartner | Array<DeliveryPartner>): Promise<void> => {
        return new Promise((resolve, reject) => {
            let partners: number[];
            if (!sender) {
                reject('No item selected');
                return;
            } else if (Array.isArray(sender)) {
                 setReload(false);
                 partners = sender.map(e => e.deliveryPartnerId!);
            } else {
                 partners = [sender.deliveryPartnerId!];
                 setReload(false);
            }
            return service.deleteAll(partners).then(() => setReload(true)).catch(err => console.error(err));
        })
    };

    const tawreed: TawreedTableProps = {
        header: true,
        pagination: true,
        dataKey: 'deliveryPartnerId',
        name: 'tbl.delivery.partners',
        title: 'lbl_delivery_partners',
        reload: reload,
        ds: {
            mode: 'lazy',
            onSearch: (req) => service.search(req),
        },
        columns: [
            {
                field: "partnerName",
                header: "lbl_partners_partner_name",
                sortable: true,
                sortField: 'partnerName',
                filter: true,
                filterField: 'partnerName',
                style: CssUtils.width('30%'),
            },
            {
                field: "codCost",
                header: "lbl_delivery_partners_codCost",
                sortable: true,
                filter: true,
                ...TawreedTableFormatters.decimal('codCost', user?.scale! ,undefined, CssUtils.width('25%')),
            },
            {
                field: "codSelling",
                header: "lbl_delivery_partners_codSelling",
                sortable: true,
                filter: true,
                ...TawreedTableFormatters.decimal('codSelling', user?.scale! , undefined, CssUtils.width('25%')),
            },
            {
                dataType: 'boolean',
                field: "active",
                header: "lbl_partners_active",
                sortable: true,
                sortField: 'active',
                filter: false,
                filterField: 'active',
                ...TawreedTableFilters.boolean('active'),
                style: CssUtils.width('20%'),
            },
        ],
        toolbar: {
            actions: [
                new TawreedAction(TawreedActionName.DeliveryPartnersCreate, 'stateless', 'lbl_new', PrimeIcons.PLUS, onNew),
                new TawreedAction(TawreedActionName.DeliveryPartnersDelete, 'statefull', (e) => tawreedTranslationService.translate('lbl_delete_all', e?.length ?? 0), PrimeIcons.TRASH, onDelete, 'p-button-danger', 'confirmDelete'),
                new TawreedAction(TawreedActionName.DeliveryPartnersActivate, 'statefull', (e) => tawreedTranslationService.translate('lbl_activate_all', e?.length ?? 0), PrimeIcons.CHECK, onActivate, 'p-button-secondary', 'confirm'),
                new TawreedAction(TawreedActionName.DeliveryPartnersDeactivate, 'statefull', (e) => tawreedTranslationService.translate('lbl_deactivate_all', e?.length ?? 0), PrimeIcons.CHECK, onDeactivate, 'p-button-secondary', 'confirm'),
            ],
        },
        row: {
            onClick: new TawreedAction(TawreedActionName.DeliveryPartnersDetails, 'statefull', 'lbl_info', PrimeIcons.INFO, onDetails, 'p-button-secondary', false),
        },
        filter: {
            filterDisplay: 'menu',
            initial: {
                'partnerName': {
                    operator: 'AND',
                    constraints: [
                        {
                            value: null,
                            matchMode: 'contains',
                        },
                    ]
                },
                'transferCost': {
                    operator: 'AND',
                    constraints: [
                        {
                            value: null,
                            matchMode: 'equals',
                        },
                    ]
                },
                'cashbackPercentage': {
                    operator: 'AND',
                    constraints: [
                        {
                            value: null,
                            matchMode: 'equals',
                        },
                    ]
                },
                'active': {
                    operator: 'AND',
                    constraints: [
                        {
                            value: null,
                            matchMode: 'equals',
                        },
                    ],
                },
            },
        },
        sort: {
            sortMode: 'single',
            initial: {
                sortField: 'deliveryPartnerId',
                sortOrder: 1,
                multiSortMeta: undefined,
            },
        },
        selection: {
            mode: 'checkbox',
        },
    }
    return (<TawreedTable  {...tawreed} />);
}